/**
 * tokens — L0 Foundations, mirrored from `backend/widget-design-kit/tokens.css`.
 *
 * The native kit renders IN the page (not in a sandboxed iframe), so we cannot
 * paste the design-kit stylesheet into a document <style> without leaking
 * `:root` variables into the whole playground. Instead the runtime root gets
 * the whole token set as INLINE custom properties and every kit component
 * reads `var(--…)` — identical names, identical values, zero global bleed.
 *
 * Only `--accent` (and the display font) may be overridden per blueprint —
 * exactly the allowlist in registry.json `tokens.note`.
 */

export const LIGHT = {
  '--bg': '#F2F2F7',
  '--surface': '#FFFFFF',
  '--surface-2': '#F7F7FA',
  '--surface-3': '#ECECF1',
  '--text': '#1C1C1E',
  '--text-2': '#4A4A4F',
  '--text-3': '#8E8E93',
  '--line': 'rgba(0,0,0,0.10)',
  '--line-soft': 'rgba(0,0,0,0.05)',
  '--accent': '#9A8E7A',
  '--accent-ink': '#FFFFFF',
  '--accent-soft': 'rgba(154,142,122,0.14)',
  '--accent-line': 'rgba(154,142,122,0.40)',
  '--good': '#248A3D',
  '--warn': '#C2410C',
  '--bad': '#C70D2C',
  '--good-soft': 'rgba(36,138,61,0.12)',
  '--warn-soft': 'rgba(194,65,12,0.12)',
  '--bad-soft': 'rgba(199,13,44,0.12)',
  '--shadow-1': '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  '--shadow-2': '0 8px 24px rgba(0,0,0,0.08)',
  '--shadow-pop': '0 16px 40px rgba(0,0,0,0.14)',
};

export const DARK = {
  '--bg': '#000000',
  '--surface': '#1C1C1E',
  '--surface-2': '#2C2C2E',
  '--surface-3': '#3A3A3C',
  '--text': '#F5F5F7',
  '--text-2': '#C9C9CE',
  '--text-3': '#8E8E93',
  '--line': 'rgba(255,255,255,0.12)',
  '--line-soft': 'rgba(255,255,255,0.06)',
  '--accent-ink': '#FFFFFF',
  '--good': '#30D158',
  '--warn': '#FF9F0A',
  '--bad': '#FF453A',
  '--good-soft': 'rgba(48,209,88,0.16)',
  '--warn-soft': 'rgba(255,159,10,0.16)',
  '--bad-soft': 'rgba(255,69,58,0.16)',
  '--shadow-1': '0 1px 3px rgba(0,0,0,0.4)',
  '--shadow-2': '0 8px 24px rgba(0,0,0,0.5)',
  '--shadow-pop': '0 16px 40px rgba(0,0,0,0.6)',
};

/** Scale tokens are theme-independent. */
export const SCALE = {
  '--r-sm': '10px',
  '--r': '16px',
  '--r-lg': '24px',
  '--r-xl': '32px',
  '--r-pill': '999px',
  '--t-xs': '11px',
  '--t-sm': '13px',
  '--t-md': '15px',
  '--t-lg': '17px',
  '--t-xl': '20px',
  '--t-2xl': '26px',
  '--t-3xl': '34px',
  '--t-display': '44px',
  '--sp-1': '4px',
  '--sp-2': '8px',
  '--sp-3': '12px',
  '--sp-4': '16px',
  '--sp-5': '24px',
  '--sp-6': '32px',
  '--font-body': '-apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif',
  '--font-display': '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
  '--font-mono': '"SF Mono", ui-monospace, "Cascadia Code", monospace',
  '--ease': 'cubic-bezier(0.22, 0.61, 0.36, 1)',
  '--ease-spring': 'cubic-bezier(0.34, 1.56, 0.64, 1)',
};

/** "#07C160" → [7,193,96]; anything unparseable → null. */
function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
}

/**
 * The full inline token style for a runtime root.
 * `accent` (the blueprint's one signature colour) also re-derives the soft /
 * line accent tints so a widget never has to author them.
 */
export function buildTokenStyle({ appearance = 'light', accent = null } = {}) {
  const dark = appearance === 'dark';
  const style = { ...LIGHT, ...SCALE, ...(dark ? DARK : {}) };
  if (dark) {
    // DARK deliberately omits --accent* so the default accent survives; its
    // dark tints are stronger than the light ones.
    style['--accent-soft'] = 'rgba(154,142,122,0.22)';
    style['--accent-line'] = 'rgba(154,142,122,0.50)';
  }
  const rgb = hexToRgb(accent);
  if (rgb) {
    const [r, g, b] = rgb;
    style['--accent'] = accent;
    style['--accent-soft'] = `rgba(${r},${g},${b},${dark ? 0.22 : 0.14})`;
    style['--accent-line'] = `rgba(${r},${g},${b},${dark ? 0.5 : 0.4})`;
  }
  style.colorScheme = dark ? 'dark' : 'light';
  style.background = 'var(--bg)';
  style.color = 'var(--text)';
  style.fontFamily = 'var(--font-body)';
  return style;
}

/**
 * THE SESSION SURFACE'S TOKENS — the same kit, with NO PERSONALITY.
 *
 * A widget has a face: its own `--font-display`, its own `--accent`, its own
 * paper. A SESSION HAS NEITHER, and that is a design rule rather than an
 * oversight (PRD §5.1): the session is where the user's own photographs are,
 * and the only colour on the surface is theirs. So the chrome is monochrome —
 * system font, paper white / ink black, `accent === ink` — and there is no
 * `accent` parameter here at all. A per-block accent is not something a
 * session block may ask for, so it is not something this function can express.
 *
 * The `--vi-*` names are the SESSION contract (`vi_components.css`, mirrored
 * by the app's `view_ir/tokens.dart`), and they are what the session block
 * components read. The kit's own `--…` tokens are then REMAPPED onto them, so
 * a primitive a block reuses (`Image`, `BeforeAfterCompare`) draws in session
 * colours without knowing which surface it is on — one token set, two names,
 * no component that has to care.
 *
 * §5.3's type scale is fixed px (never `clamp`), §5.4's corners are 16 outer /
 * 12 inner, and `--vi-gutter` is the surface's own reading margin — the one
 * number `hero_media` needs in order to bleed out of it.
 */
export const SESSION_LIGHT = {
  '--vi-bg': '#FFFFFF',
  '--vi-surface': '#FFFFFF',
  '--vi-surface-muted': '#F6F7F9',
  '--vi-border': '#E5E7EB',
  '--vi-text': '#111111',
  '--vi-text-secondary': '#3F4652',
  '--vi-text-muted': '#8A93A3',
  '--vi-accent': '#111111',
  '--vi-accent-text': '#FFFFFF',
};

export const SESSION_DARK = {
  '--vi-bg': '#000000',
  '--vi-surface': '#0B0B0C',
  '--vi-surface-muted': '#161618',
  '--vi-border': 'rgba(255,255,255,0.14)',
  '--vi-text': '#F7F7F7',
  '--vi-text-secondary': '#C6C9CF',
  '--vi-text-muted': '#8A93A3',
  '--vi-accent': '#F7F7F7',
  '--vi-accent-text': '#000000',
};

/** Session scale tokens — theme-independent, exactly like the kit's SCALE. */
export const SESSION_SCALE = {
  '--vi-t-2xl': '28px',
  '--vi-t-xl': '22px',
  '--vi-t-lg': '18px',
  '--vi-t-base': '15px',
  '--vi-t-sm': '13px',
  '--vi-t-xs': '11px',
  '--vi-r': '16px',
  '--vi-r-inner': '12px',
  '--vi-gutter': '24px',
  '--vi-shadow': '0 1px 4px rgba(0,0,0,0.06)',
  // Ink laid OVER a picture is not on a theme surface — it is on the
  // photograph, which is as dark in light mode as it is in dark. The one pair
  // in the session set that is identical in both themes, for the same reason
  // the kit's MEDIA_INK is.
  '--vi-on-media': '#FFFFFF',
  '--vi-scrim': 'rgba(12,12,13,0.62)',
};

/** The inline token style for a SESSION root: `--vi-*` + the kit remap. */
export function buildSessionTokenStyle({ appearance = 'light' } = {}) {
  const dark = appearance === 'dark';
  const vi = { ...SESSION_LIGHT, ...SESSION_SCALE, ...(dark ? SESSION_DARK : {}) };
  const style = {
    ...SCALE,
    ...vi,
    // the kit's names, pointed at the session's values
    '--bg': 'var(--vi-bg)',
    '--surface': 'var(--vi-surface)',
    '--surface-2': 'var(--vi-surface-muted)',
    '--surface-3': 'var(--vi-surface-muted)',
    '--text': 'var(--vi-text)',
    '--text-2': 'var(--vi-text-secondary)',
    '--text-3': 'var(--vi-text-muted)',
    '--line': 'var(--vi-border)',
    '--line-soft': 'var(--vi-border)',
    // accent IS ink here — there is no second colour to be had
    '--accent': 'var(--vi-accent)',
    '--accent-ink': 'var(--vi-accent-text)',
    '--accent-soft': 'var(--vi-surface-muted)',
    '--accent-line': 'var(--vi-border)',
    '--r-sm': 'var(--vi-r-inner)',
    '--r': 'var(--vi-r)',
    '--t-xs': 'var(--vi-t-xs)',
    '--t-sm': 'var(--vi-t-sm)',
    '--t-md': 'var(--vi-t-base)',
    '--t-lg': 'var(--vi-t-lg)',
    '--t-xl': 'var(--vi-t-xl)',
    '--t-2xl': 'var(--vi-t-2xl)',
    // a session NEVER sets a display face
    '--font-display': SCALE['--font-body'],
    '--shadow-1': 'var(--vi-shadow)',
  };
  style.colorScheme = dark ? 'dark' : 'light';
  style.background = 'var(--vi-bg)';
  style.color = 'var(--vi-text)';
  style.fontFamily = 'var(--font-body)';
  return style;
}

/** Token → CSS value for the status tones the kit uses by name. */
export const TONE_VAR = {
  good: 'var(--good)',
  warn: 'var(--warn)',
  bad: 'var(--bad)',
  muted: 'var(--text-3)',
  text: 'var(--text)',
  'text-2': 'var(--text-2)',
  'text-3': 'var(--text-3)',
  accent: 'var(--accent)',
  // ink ON the accent (a filled accent card's text); `-2` is its quieter line
  'accent-ink': 'var(--accent-ink)',
  'accent-ink-2': 'color-mix(in srgb, var(--accent-ink) 78%, transparent)',
};

export const TONE_SOFT_VAR = {
  good: 'var(--good-soft)',
  warn: 'var(--warn-soft)',
  bad: 'var(--bad-soft)',
  muted: 'var(--surface-3)',
  accent: 'var(--accent-soft)',
};
